import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaUserInjured, FaSearch, FaUserMd, FaBrain, FaPhone, FaEnvelope } from 'react-icons/fa';
import './Healthcare.css';

const Patients = () => {
  const { t } = useTranslation();
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const token = localStorage.getItem('access_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/healthcare/patients', { headers, params: search.trim() ? { search: search.trim() } : {} });
      setPatients(res.data?.patients || []);
    } catch (err) {
      console.error('Failed to load patients:', err);
    } finally {
      setLoading(false);
    }
  }, [headers, search]);

  useEffect(() => { fetchPatients(); /* eslint-disable-next-line */ }, []);

  const openPatient = async (id) => {
    setDetailLoading(true);
    try {
      const res = await axios.get(`/api/healthcare/patients/${id}`, { headers });
      setDetail(res.data);
    } catch (err) {
      console.error('Failed to load patient:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="hc-page">
      <header className="hc-header">
        <div className="hc-header-title">
          <FaUserInjured className="hc-header-icon" />
          <div>
            <h1>{t('patients.title', 'Patients')}</h1>
            <p>{t('patients.subtitle', 'Your patient population, their appointments and memory.')}</p>
          </div>
        </div>
      </header>

      <form className="hc-controls" onSubmit={(e) => { e.preventDefault(); fetchPatients(); }}>
        <div className="hc-search"><FaSearch /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('patients.searchPlaceholder', 'Search by name or phone')} /></div>
        <button type="submit" className="hc-btn primary">{t('patients.search', 'Search')}</button>
      </form>

      <div className="hc-layout">
        <section className="hc-list-card">
          {loading ? (
            <div className="hc-state">{t('patients.loading', 'Loading...')}</div>
          ) : patients.length === 0 ? (
            <div className="hc-state">{t('patients.empty', 'No patients yet.')}</div>
          ) : (
            patients.map((p) => (
              <button type="button" key={p.id} className={`hc-patient-row${detail && detail.id === p.id ? ' sel' : ''}`} onClick={() => openPatient(p.id)}>
                <span className="hc-patient-name">{p.name}</span>
                <span className="hc-patient-phone">{p.phone_number}</span>
                {p.doctor_count > 0 && <span className="hc-patient-docs"><FaUserMd /> {p.doctor_count}</span>}
              </button>
            ))
          )}
        </section>

        <aside className="hc-detail-card">
          {detailLoading ? (
            <div className="hc-state">{t('patients.loading', 'Loading...')}</div>
          ) : !detail ? (
            <div className="hc-state">{t('patients.select', 'Select a patient to see details.')}</div>
          ) : (
            <>
              <h2>{detail.name}</h2>
              <div className="hc-detail-meta">
                {detail.phone_number && <span><FaPhone /> {detail.phone_number}</span>}
                {detail.email && <span><FaEnvelope /> {detail.email}</span>}
                {detail.address && <span>{detail.address}</span>}
              </div>

              {detail.doctors?.length > 0 && (
                <div className="hc-block">
                  <h4><FaUserMd /> {t('patients.doctors', 'Doctors')}</h4>
                  {detail.doctors.map((d) => <span key={d.id} className="hc-tag">Dr {d.name}{d.speciality ? ` · ${d.speciality}` : ''}</span>)}
                </div>
              )}

              {detail.memory && (
                <div className="hc-block">
                  <h4><FaBrain /> {t('patients.memory', 'Memory')}</h4>
                  {detail.memory.summary && <p className="hc-summary">{detail.memory.summary}</p>}
                  <span className="hc-muted">{detail.memory.call_count || 0} {t('patients.calls', 'calls')}</span>
                </div>
              )}

              <div className="hc-block">
                <h4>{t('patients.appointments', 'Appointments')}</h4>
                {(!detail.appointments || detail.appointments.length === 0) ? (
                  <span className="hc-muted">{t('patients.noAppointments', 'No appointments.')}</span>
                ) : (
                  detail.appointments.map((a) => (
                    <div key={a.id} className={`hc-appt${a.status === 'cancelled' ? ' cancelled' : ''}`}>
                      <span>{a.date} · {a.time} · Dr {a.doctor_name}{a.appointment_type ? ` · ${t('patients.type.' + a.appointment_type, a.appointment_type)}` : ''}</span>
                      {a.reason && <span className="hc-appt-reason">{a.reason}</span>}
                      {a.status === 'cancelled' && <span className="hc-cancel-flag">{t('patients.cancelled', 'Cancelled')}</span>}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </aside>
      </div>
    </div>
  );
};

export default Patients;
